// Mentor context assembler — grounds every mentor-grade prompt in the user's real
// goal, plan phase, journal entries, and progress numbers. Every number here is
// TRUTHFUL: it comes from supplied data or a null-safe read, never invented.
// This module reads only structuredEntries/{uid} and users/{uid}/progress/{goalId};
// it never touches any Memory Highway node.
import { ref, get } from 'firebase/database';
import { database } from '../firebase';

// Format a structured journal entry into readable text for the AI. Moved verbatim
// from GlassEditor's formatStructuredEntryForAI so the logic lives in exactly one place.
export function formatStructuredEntry(entry) {
  if (!entry.formData) return entry.summary || '';

  const data = entry.formData[entry.primaryType];
  let formatted = '';

  switch (entry.primaryType) {
    case 'daily':
      formatted = `
ONE THING FOCUS: ${data.oneThingFocus}
HIGH-IMPACT GOALS: ${data.highImpactGoals.filter(g => g.trim()).join(', ')}
DEEP WORK: Morning: ${data.deepWorkBlocks.morning}, Afternoon: ${data.deepWorkBlocks.afternoon}
QUARTERLY PROGRESS: ${data.quarterlyProgress.filter(p => p.trim()).join(', ')}
WINS: ${data.wins}
OBSTACLES: ${data.obstacles.challenge} - SOLUTION: ${data.obstacles.solution}
LEARNING: ${data.learning}
TOMORROW'S PRIORITIES: ${data.tomorrowPriorities.filter(p => p.trim()).join(', ')}
REFLECTION: ${data.reflection}
        `.trim();
      break;

    case 'meeting':
      formatted = `
ATTENDEES: ${data.attendees}
AGENDA: ${data.agenda.filter(a => a.trim()).join(', ')}
KEY DECISIONS: ${data.keyDecisions}
ACTION ITEMS: ${data.actionItems.filter(a => a.trim()).join(', ')}
NEXT STEPS: ${data.nextSteps}
INSIGHTS: ${data.insights}
        `.trim();
      break;

    case 'project':
      formatted = `
PROJECT: ${data.projectName}
OBJECTIVE: ${data.objective}
SUCCESS DEFINITION: ${data.successDefinition}
BUILD-MEASURE-LEARN: Build: ${data.leanApproach.build}, Measure: ${data.leanApproach.measure}, Learn: ${data.leanApproach.learn}
TIMELINE: ${data.timeline.filter(t => t.trim()).join(', ')}
RESOURCES: Team: ${data.resources.team}, Budget: ${data.resources.budget}, Tools: ${data.resources.tools}
METRICS: ${data.metrics.objective} - KR1: ${data.metrics.kr1}, KR2: ${data.metrics.kr2}, KR3: ${data.metrics.kr3}
RISKS: ${data.risks.filter(r => r.trim()).join(', ')}
WHY-HOW-WHAT: Why: ${data.stakeholders.why}, How: ${data.stakeholders.how}, What: ${data.stakeholders.what}
        `.trim();
      break;

    default:
      formatted = entry.summary || '';
  }

  return formatted;
}

// Render the user's most recent structured journal entries as compact lines.
// If entries are not supplied, read them once from structuredEntries/{uid}.
export async function buildRecentEntriesSummary(uid, { entries = null, limit = 10 } = {}) {
  if (!entries) {
    const snap = await get(ref(database, `structuredEntries/${uid}`));
    entries = snap.val() || {};
  }

  const items = Object.entries(entries).map(([id, e]) => ({
    id,
    date: e.createdAt || e.date,
    primaryType: e.primaryType,
    text: formatStructuredEntry(e) || e.summary || ''
  }));

  items.sort((a, b) => new Date(a.date) - new Date(b.date));
  const recent = items.slice(-limit);

  if (recent.length === 0) return 'No recent journal entries.';

  return recent
    .map(it => {
      const d = new Date(it.date);
      const dateStr = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      const text = it.text.length > 400 ? it.text.slice(0, 400) : it.text;
      return `- [${dateStr}] ${it.primaryType}: ${text}`;
    })
    .join('\n');
}

// Assemble the single mentor-context block: who the mentor is helping, their plan
// phase, TRUTHFUL progress numbers, today's action, and their recent journal entries.
// completed/total/pace come from supplied progressData, else from a null-safe read of
// users/{uid}/progress/{goalId} (raw stored fields until W11 makes them truthful).
export async function buildMentorContext(uid, { goal, businessPlan, entries, progressData, todaysAction } = {}) {
  const currentPhase = businessPlan?.phases?.[businessPlan?.progress?.currentPhase || 0]?.title;

  let completed = 0;
  let total = 0;
  let pace = null;

  if (progressData) {
    completed = progressData.completedTasks || 0;
    total = progressData.totalTasks || 0;
    pace = progressData.pace || null;
  } else if (goal?.id) {
    try {
      const snap = await get(ref(database, `users/${uid}/progress/${goal.id}`));
      const pd = snap.val() || {};
      completed = pd.completedTasks || 0;
      total = pd.totalTasks || 0;
      pace = pd.pace || null;
    } catch (_) {
      // null-safe: leave truthful defaults (0/0, no pace)
    }
  }

  const recentEntries = await buildRecentEntriesSummary(uid, { entries });

  return `## WHO YOU ARE MENTORING
Goal: ${goal?.title || goal?.goal || 'Not set'} — ${goal?.description || ''}
Plan: ${businessPlan?.title || 'No plan yet'}${currentPhase ? ` — current phase: ${currentPhase}` : ''}
Progress: ${completed}/${total} tasks complete${pace ? ` (day ${pace.day} of ${pace.totalDays})` : ''}
Today's action: ${todaysAction || 'Not generated yet'}

## RECENT JOURNAL ENTRIES (last 10)
${recentEntries}`;
}
